import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ActiveNexusFilter,
  GanttChartData,
  GanttTabBarTab,
  PondooStatus,
  TaskDependency,
} from "../../types/ganttChart";
import {
  getGanttChartData,
  getPondooStatus,
  initiateGanttChartData,
  initiateNexusLandingDataExtraction,
  initiateOptimization,
} from "./action";
import labels from "../../utils/labels";
import { reorderEmployeesInPlace } from "./employeeOrderingUtils";
import TeamColorGroupUtils, {
  TeamGroupColorMaps,
} from "../../utils/TeamColorGroupUtils";

type GanttChartTabAction = {
  payload: GanttTabBarTab;
};

type PendingDataFilters = { filter: string; date: string; requestId?: string };

export interface GanttChartSlice {
  selectedGanttChartTab: GanttTabBarTab;
  verticalColumnWidth: number;
  unplannedColumnWidth: number;
  verticalSvgWidth: number;
  selectedActiveNexusFilter?: ActiveNexusFilter;
  translateXValueInPercent: number;
  ganttChartData?: GanttChartData;
  pondooStatus?: PondooStatus;
  pondooStatusError?: string;
  showSpinner: boolean;
  error?: string;
  pendingData: PendingDataFilters[];
  clickedTaskDependencies?: TaskDependency[];
  scenarioIdToInitiatePondooSolution?: number;
  teamGroupColorMaps?: TeamGroupColorMaps;
  showSpinnerOnCustomData: boolean;
  currentRequestId?: string;
  previouslyLockedRouteShiftIds: number[];
}

const initialState: GanttChartSlice = {
  selectedGanttChartTab: "Original",
  verticalColumnWidth: 0,
  unplannedColumnWidth: 0,
  verticalSvgWidth: 0,
  translateXValueInPercent: 0,
  showSpinner: false,
  pendingData: [],
  showSpinnerOnCustomData: false,
  previouslyLockedRouteShiftIds: [],
};

const ganttChartSSlice = createSlice({
  name: "gantt-chart",
  initialState,
  reducers: {
    setSelectedGanttChartTab(state, action: GanttChartTabAction) {
      state.selectedGanttChartTab = action.payload;
    },
    setVerticalColumnWidth(state, action) {
      state.verticalColumnWidth = action.payload;
    },
    setUnplannedColumnWidth(state, action) {
      state.unplannedColumnWidth = action.payload;
    },
    setVerticalSvgWidth(state, action) {
      state.verticalSvgWidth = action.payload;
    },
    clearGanttChartData(state) {
      delete state.ganttChartData;
    },
    setShowSpinner(state) {
      state.showSpinner = true;
    },
    setHideSpinner(state) {
      state.showSpinner = false;
    },
    setClickedTaskDependencies(state, action) {
      state.clickedTaskDependencies = action.payload;
    },
    deleteClickedTaskDependencies(state) {
      delete state.clickedTaskDependencies;
    },
    resetGanttChartErrorMessage(state) {
      delete state.error;
    },
    setGanttChartErrorMessage(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    clearPondooStatusData(state) {
      state.pondooStatusError = labels.pondooMissing;
    },

    updateLockedForPlanning(state, action) {
      // Updates the lockedForPlanning property on the employee object in the ganttChartData, without reloading everything from the server.
      const { idShift, lockedForPlanning } = action.payload;

      if (state.ganttChartData?.employees) {
        const employee = state.ganttChartData.employees.find(
          (employee) => employee.shiftSchedule.idShift === idShift,
        );
        if (employee) {
          employee.shiftSchedule.lockedForPlanning = lockedForPlanning;
        }
      }
    },
    setAllRoutesAsLockedForPlanning(state) {
      const employees = state.ganttChartData?.employees;
      if (!employees) return;

      const lockedShiftIds: number[] = [];

      //Locate all currently locked routes for storing, before updating to all locked.
      employees.forEach((employee) => {
        const shiftSchedule = employee.shiftSchedule;
        if (!shiftSchedule) return;

        if (shiftSchedule.lockedForPlanning) {
          lockedShiftIds.push(shiftSchedule.idShift);
        }

        shiftSchedule.lockedForPlanning = true;
      });

      state.previouslyLockedRouteShiftIds = lockedShiftIds;
    },
    undoLockAllRoutes(state) {
      const employees = state.ganttChartData?.employees;
      if (!employees) return;

      const lockedShiftIds = state.previouslyLockedRouteShiftIds;

      employees.forEach((employee) => {
        const shiftSchedule = employee.shiftSchedule;
        if (!shiftSchedule) return;

        shiftSchedule.lockedForPlanning = lockedShiftIds.includes(
          shiftSchedule.idShift,
        );
      });

      state.previouslyLockedRouteShiftIds = [];
    },
    setGanttChartData(state, action: PayloadAction<GanttChartData>) {
      state.ganttChartData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initiateGanttChartData.pending, (state, action) => {
      delete state.ganttChartData;
      delete state.scenarioIdToInitiatePondooSolution;
      const filter = new URL(window.location.href).searchParams.get("filter");
      const date = new URL(window.location.href).searchParams.get("date");
      state.pendingData.push({
        filter: filter!,
        date: date!,
        requestId: action.meta.requestId,
      });
    });
    builder.addCase(initiateGanttChartData.fulfilled, (state, action) => {
      state.pendingData = state.pendingData.filter(
        (pendingDataObj) => pendingDataObj.requestId !== action.meta.requestId,
      );
      state.scenarioIdToInitiatePondooSolution = action.payload.idScenario;
    });
    builder.addCase(initiateGanttChartData.rejected, (state, action) => {
      // Remove spinner for requestID if request fails
      state.pendingData = state.pendingData.filter(
        (pendingDataObj) => pendingDataObj.requestId !== action.meta.requestId,
      );
    });
    builder.addCase(
      initiateNexusLandingDataExtraction.pending,
      (state, action) => {
        delete state.ganttChartData;
        delete state.scenarioIdToInitiatePondooSolution;
        const filter = new URL(window.location.href).searchParams.get("filter");
        const date = new URL(window.location.href).searchParams.get("date");
        state.pendingData.push({
          filter: filter!,
          date: date!,
          requestId: action.meta.requestId,
        });
      },
    );
    builder.addCase(
      initiateNexusLandingDataExtraction.fulfilled,
      (state, action) => {
        // Remove spinner when data is ready
        state.pendingData = state.pendingData.filter(
          (pendingDataObj) =>
            pendingDataObj.requestId !== action.meta.requestId,
        );
        state.scenarioIdToInitiatePondooSolution = action.payload.idScenario;
      },
    );
    builder.addCase(
      initiateNexusLandingDataExtraction.rejected,
      (state, action) => {
        // Remove spinner for requestID if request fails
        state.pendingData = state.pendingData.filter(
          (pendingDataObj) =>
            pendingDataObj.requestId !== action.meta.requestId,
        );
        state.showSpinner = false;
        state.error = "Noget gik galt i forbindelsen til Nexus.";
      },
    );
    builder.addCase(getGanttChartData.fulfilled, (state, action) => {
      state.showSpinner = false;

      /*
       Rapidly switching between ganttChart tabs will cause the data to become out of sync. 
       To fix this, we ensure that data is only updated when the solutionsType matches the selected ganttChartTab.*/
      const tab =
        state.selectedGanttChartTab === "Optimised" ? "Pondoo" : "Nexus";

      if (state.ganttChartData && action.payload.solutionType !== tab) return;
      const ganttChart = reorderEmployeesInPlace(action.payload!);
      state.ganttChartData = ganttChart;

      if (ganttChart.useColorGroupingsAsTeamSplitter) {
        state.teamGroupColorMaps =
          TeamColorGroupUtils.computeTeamColorGroupMaps(ganttChart.employees);
      }
    });
    builder.addCase(getGanttChartData.pending, (state, action) => {
      state.currentRequestId = action.meta.requestId; // track latest request to avoid toggling spinner when old request is rejected, but new is pending.
      delete state.ganttChartData;
      delete state.teamGroupColorMaps;
      delete state.error;
      state.showSpinner = true;
    });
    builder.addCase(getGanttChartData.rejected, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.showSpinner = false;
      state.currentRequestId = undefined;
      const errorMessage = action.error.message;
      if (errorMessage?.includes("404")) {
        // state.error =
        //   "Noget gik galt, da vi forsøgte at hente din plan. Prøv igen.";
      } else {
        state.error =
          "Noget gik galt, da vi forsøgte at hente din plan. Prøv igen.";
      }
    });
    builder.addCase(getPondooStatus.pending, (state) => {
      delete state.pondooStatusError;
      delete state.pondooStatus;
    });
    builder.addCase(getPondooStatus.fulfilled, (state, action) => {
      state.pondooStatus = action.payload;
    });
    builder.addCase(getPondooStatus.rejected, (state, action) => {
      state.pondooStatusError = labels.pondooStatusError;
      // Todo handle error on status bar.
    });
    builder.addCase(initiateOptimization.pending, (state) => {});
    builder.addCase(initiateOptimization.rejected, (state) => {
      // Todo handle error on status bar.
    });
    builder.addCase(initiateOptimization.fulfilled, (state, action) => {
      console.log(action.payload);
    });
  },
});

export const {
  setSelectedGanttChartTab,
  setVerticalColumnWidth,
  setUnplannedColumnWidth,
  setVerticalSvgWidth,
  clearGanttChartData,
  setShowSpinner,
  setHideSpinner,
  setClickedTaskDependencies,
  deleteClickedTaskDependencies,
  setGanttChartErrorMessage,
  resetGanttChartErrorMessage,
  clearPondooStatusData,
  updateLockedForPlanning,
  setGanttChartData,
  setAllRoutesAsLockedForPlanning,
  undoLockAllRoutes,
} = ganttChartSSlice.actions;

export default ganttChartSSlice.reducer;
